import {
  ArticleIcon,
  BrowserIcon,
  ChatCircleIcon,
  FileTextIcon,
  FilmSlateIcon,
  ImageIcon,
  NoteIcon,
} from '@phosphor-icons/react';
import type { ComponentType } from 'react';

/**
 * What a thing on the Content Desk IS.
 *
 * 🔴 THE VOCABULARY IS THE FOUNDER'S, VERBATIM (2026-09-06). Do not add,
 * rename or "improve" a value without asking him. The reason is written into
 * `20260904220000_calendar_moments.sql`, which deliberately shipped with NO
 * type column rather than invent one: a made-up enum gets taught back to the
 * founder as if it were his own vocabulary, and then the product speaks a
 * language nobody chose. That migration also named its own reversal condition
 * — "a sub-type earns its place the day someone needs to FILTER or REPORT by
 * one" — and the Desk is that day, so the column is now justified. The values
 * below are the ones he gave when asked.
 *
 * `proposal` is first on purpose and is not a stage. It is the type of a thing
 * that has been PROPOSED and not yet made — an editorial topic sent to a
 * client as "shall we write this?". Those are the 32 rows the client actually
 * sees today, and calling them "Planned post" (a post, with no post in them)
 * is the labelling defect this module exists to end.
 */
export const DESK_CONTENT_TYPES = [
  'proposal',
  'post',
  'article',
  'video',
  'graphic',
  'document',
  /**
   * 🔴 ADDED 2026-09-09, WITH THE FOUNDER'S EXPLICIT ANSWER, NOT AROUND HIM.
   *
   * The header above forbids adding a value without asking. He was asked, and
   * answered: _"i think maybe call web page or somethign but i will let you
   * decide here."_ He had already used the noun himself the same day —
   * "staged link for site page" — so the word is his, not one invented and
   * taught back to him.
   *
   * Reusing `document` was the cheaper option and was rejected. Its meaning
   * line does say "a brief, a deck, or a page", but a live page on the
   * client's own website is not a document: you do not read it in Mora, you
   * open it on their site, and it can change after you approve it. Trap #2 in
   * the desk README is precisely about labels that do not describe the thing.
   *
   * ⚠️ `project_tasks.type` already permits the string 'page' for something
   * adjacent (a PLANNED page). They cannot collide, because `deriveContentType`
   * deliberately never reads that column — but the word now exists twice, and
   * that was noticed rather than missed.
   */
  'page',
] as const;

export type DeskContentType = (typeof DESK_CONTENT_TYPES)[number];

export const DESK_CONTENT_TYPE_LABEL: Record<DeskContentType, string> = {
  proposal: 'Proposal',
  post: 'Post',
  article: 'Article',
  video: 'Video',
  graphic: 'Graphic',
  document: 'Document',
  page: 'Web page',
};

/**
 * The canonical type glyph — design-system rule 26: an object's icon is
 * chosen ONCE, here, never picked ad hoc per call site. Promoted 2026-09-09
 * from `DeskRow.tsx`, which defined this same map privately; that file's own
 * doc comment already states the rule this map exists to enforce:
 * "Content-type icons are NEVER coloured (design system Law 2: 'colour NEVER
 * for: content-type icons') — they are a label, not a state." Every consumer
 * (`DeskRow`, `CalendarMomentBar`, the mobile week view) renders this at
 * `weight="regular"` in a neutral/muted ink — never a hue keyed to the type.
 */
export const DESK_CONTENT_TYPE_ICON: Record<
  DeskContentType,
  ComponentType<{ className?: string; weight?: 'regular' }>
> = {
  proposal: NoteIcon,
  post: ChatCircleIcon,
  article: ArticleIcon,
  video: FilmSlateIcon,
  graphic: ImageIcon,
  // `FileTextIcon`, not `NoteIcon` — the two rendered byte-identical SVG
  // path data (confirmed live 2026-09-09) whenever a `proposal` and a
  // `document` collided on a surface with no word beside the glyph (the
  // calendar bar), which is exactly the "can't tell what is what" gap this
  // whole map exists to close.
  document: FileTextIcon,
  /*
   * A browser frame, not a link glyph. The row already tells the reader this
   * opens somewhere; what the icon has to say is WHAT the thing is — a page
   * on a website — which is the same job every other icon in this map does.
   * A link icon would describe the action twice and the object never.
   */
  page: BrowserIcon,
};

/**
 * 🔴 A TYPE NAME THAT NEEDS EXPLAINING IS NOT DOING ITS JOB ON ITS OWN.
 *
 * The founder chose this vocabulary and then, seeing it on 32 cards, asked:
 * *"What even is a proposal?"* Both facts are real. The word is right — these
 * ARE proposed pieces nobody has made yet — and a one-word eyebrow cannot carry
 * a definition. So the definition travels with it, as a tooltip on the card and
 * a line in the panel, rather than the label being changed again or the
 * question being left standing.
 *
 * Written for the client, not for us: they are the ones deciding.
 */
export const DESK_CONTENT_TYPE_MEANING: Record<DeskContentType, string> = {
  proposal:
    'An idea we think is worth making. Nothing has been written yet — you are deciding whether it should be.',
  post: 'A social post, drafted and ready for you to read.',
  article: 'A written article. Open it to read the whole thing.',
  video: 'A video, or something being filmed.',
  graphic: 'An image or design.',
  document: 'A document — a brief, a deck, or a page.',
  /*
   * Written for the reader, and honest about the one thing that makes a page
   * different from everything else on this desk: it lives somewhere Mora does
   * not control, so it can change after they read it. The card carries the
   * full caveat; this line just sets the expectation that they are leaving.
   */
  page: 'A page on your website. Open it on your site to read it, then tell us if it is good to go live.',
};

export function isDeskContentType(value: unknown): value is DeskContentType {
  return typeof value === 'string' && (DESK_CONTENT_TYPES as readonly string[]).includes(value);
}

export interface DeskContentTypeInput {
  /** `project_tasks.artifact_kind` — non-null once the artifact actually exists. */
  artifactKind?: string | null;
  /** `calendar_moments.content_type`, the operator's own hand-typed choice. */
  momentType?: string | null;
  /** True when the row is a `social_posts` draft rather than a plan row. */
  isSocialPost?: boolean;
  /**
   * `social_posts.media_type` — the FORMAT axis, and the one the desk was
   * blind to.
   *
   * 🔴 THIS IS NOT `post_type`, AND CONFLATING THEM IS THE TRAP. `post_type`
   * is the commercial axis: measured across prod it is `organic` (1,925),
   * `post` (102), `standard` (96), `reel` (12), `text` (6) — paid-vs-organic
   * with two stray format words mixed in. `media_type` is the medium, and it
   * is real and populated: image 1,319 · video 99 · text 50 · carousel 48.
   *
   * Until now `deriveContentType` never read it, so a filmed piece — a
   * podcast-style video, the case the founder raised — rendered on the desk
   * and its calendar as "Post", indistinguishable from a text update. A client
   * approving a video and a client approving a caption are doing different
   * work, and the calendar was showing them the same word.
   */
  mediaType?: string | null;
  /** True when the row is a `media` asset. */
  isMedia?: boolean;
  /**
   * True when `approval_items.item_kind = 'page'` — a live web page on the
   * client's own site.
   *
   * 🔴 THIS IS THE ONE PLACE `item_kind` LEGITIMATELY REACHES THIS FUNCTION,
   * and it has to, because a page has NO artifact to infer from: no
   * `artifact_kind`, no `social_posts` row, no media. Every branch below would
   * miss it and it would fall through to `proposal` — whose meaning reads
   * "Nothing has been written yet — you are deciding whether it should be",
   * printed under a page that is already built and staged.
   */
  isExternalPage?: boolean;
}

/**
 * ONE derivation, so a card cannot say "Article" on the board and "Post" in the
 * panel. Order matters and is documented rather than merely coded:
 *
 * 1. An explicit hand-typed choice always wins — the operator said so.
 * 2. A real artifact wins over a plan's intention: a `document` on the row
 *    means the article EXISTS, whatever the plan originally called for.
 * 3. Everything left is a `proposal` — proposed, not yet made. This is the
 *    branch that catches an editorial topic, and it is the whole reason
 *    `proposal` is in the vocabulary.
 *
 * `project_tasks.type` is deliberately NOT an input; the closing comment says why.
 */
/**
 * The FORMAT a social post is in, when the medium changes what the reader is
 * being asked to judge.
 *
 * Only `video` promotes. A carousel and a single image are both "a post with a
 * picture" to someone approving it — splitting them would add a word without
 * adding a decision — but watching something is not reading something, and the
 * founder's podcast-style videos are the case that proves it. `text` stays a
 * post because that is the unmarked default, not a distinct medium.
 */
function mediaFormat(mediaType: string | null | undefined): DeskContentType | null {
  return mediaType === 'video' ? 'video' : null;
}

export function deriveContentType(input: DeskContentTypeInput): DeskContentType {
  if (isDeskContentType(input.momentType)) return input.momentType;
  // Before every artifact branch: a page HAS no artifact, so any branch that
  // reads one would miss it and drop it into `proposal`.
  if (input.isExternalPage) return 'page';
  if (input.isMedia) return 'graphic';

  if (input.artifactKind === 'document') return 'article';
  if (input.artifactKind === 'media') return 'graphic';
  if (input.artifactKind === 'social_post') return mediaFormat(input.mediaType) ?? 'post';

  if (input.isSocialPost) return mediaFormat(input.mediaType) ?? 'post';

  /*
   * 🔴 NOTHING EXISTS YET, SO IT IS A PROPOSAL — whatever the plan calls it.
   *
   * `project_tasks.type` is the planner's INTENTION, not a description of a
   * thing that exists. Every one of the 70 editorial rows in the first real
   * workspace carries `type = 'post'`, so consulting it here labelled 70
   * un-written topics "Post" — a softer version of the exact "PLANNED POST
   * with no post in it" defect this vocabulary was introduced to end.
   *
   * The intention is not thrown away: the moment an artifact lands, the
   * branches above read it and the card becomes an Article, a Post or a
   * Graphic. Until then the honest word is the one the founder put first.
   */
  return 'proposal';
}
